import * as THREE from 'three';

const SHADOW_RECEIVER_NAMES = new Set([
   'BodenPlatte',
   'secondground',
   'Kanal_main',
   'FluessigbodenPlane_centerLeft',
   'FluessigbodenPlane_moving',
   'MudWallCenter',
   'Street',
]);

const KIES_PLATTE_OFFICE_TEXTURE_SCALE = 8;
const KIES_PLATTE_OFFICE_NOISE_SCALE = 1.7;

function createNoiseTexture() {
   const size = 256;
   const cells = 16;
   const cellSize = size / cells;
   const canvas = document.createElement('canvas');
   canvas.width = size;
   canvas.height = size;

   const context = canvas.getContext('2d');

   for (let row = 0; row < cells; row += 1) {
      for (let column = 0; column < cells; column += 1) {
         const noise = 64 + Math.floor(Math.random() * 128);
         context.fillStyle = `rgb(${noise}, ${noise}, ${noise})`;
         context.fillRect(
            column * cellSize,
            row * cellSize,
            cellSize,
            cellSize,
         );
      }
   }

   const texture = new THREE.CanvasTexture(canvas);
   texture.wrapS = THREE.RepeatWrapping;
   texture.wrapT = THREE.RepeatWrapping;
   texture.colorSpace = THREE.NoColorSpace;

   return texture;
}

function createKiesPlatteOfficeMaterial(material) {
   const scaledMaterial = material.clone();

   for (const mapName of ['map', 'normalMap', 'roughnessMap']) {
      if (!scaledMaterial[mapName]) continue;

      scaledMaterial[mapName] = scaledMaterial[mapName].clone();
      scaledMaterial[mapName].repeat.multiplyScalar(
         KIES_PLATTE_OFFICE_TEXTURE_SCALE,
      );
   }

   const noiseTexture = createNoiseTexture();
   const noiseUniforms = {
      scale: { value: KIES_PLATTE_OFFICE_NOISE_SCALE },
      strength: { value: 0.56 },
   };
   scaledMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.kiesOfficeNoiseMap = { value: noiseTexture };
      shader.uniforms.kiesOfficeNoiseScale = noiseUniforms.scale;
      shader.uniforms.kiesOfficeNoiseStrength = noiseUniforms.strength;
      shader.vertexShader = `
         varying vec2 vKiesOfficeNoiseUv;
         ${shader.vertexShader}`;
      shader.vertexShader = shader.vertexShader.replace(
         '#include <uv_vertex>',
         `
            #include <uv_vertex>
            vKiesOfficeNoiseUv = uv;
         `,
      );
      shader.fragmentShader = `
         uniform sampler2D kiesOfficeNoiseMap;
         uniform float kiesOfficeNoiseScale;
         uniform float kiesOfficeNoiseStrength;
         varying vec2 vKiesOfficeNoiseUv;
         ${shader.fragmentShader}`;
      shader.fragmentShader = shader.fragmentShader.replace(
         '#include <map_fragment>',
         `
            #include <map_fragment>
            float noise = texture2D(kiesOfficeNoiseMap, vKiesOfficeNoiseUv * kiesOfficeNoiseScale).r;
            diffuseColor.rgb *= 1.0 + (noise - 0.5) * kiesOfficeNoiseStrength;
            diffuseColor.rgb *= 1.2;
         `,
      );
   };
   scaledMaterial.customProgramCacheKey = () => 'kies-plate-office-noise';
   scaledMaterial.kiesOfficeNoiseUniforms = noiseUniforms;

   return scaledMaterial;
}

export function applyCustomMaterials(root, materials) {
   root.traverse((object) => {
      if (!object.isMesh) return;

      object.castShadow = true;
      object.receiveShadow =
         SHADOW_RECEIVER_NAMES.has(object.name) ||
         object.name.startsWith('MudWall') ||
         object.name.startsWith('Kies');

      const replacement = object.name.startsWith('MudWall')
         ? materials.MudWallCenter
         : object.name.startsWith('FluessigbodenPlane') &&
             object.name !== 'FluessigbodenPlane_moving'
           ? materials.FluessigbodenPlane
           : object.name.startsWith('Kies')
             ? materials.Kies
             : object.name.startsWith('gras')
               ? materials.Gras
               : object.name.startsWith('PileSand_01')
                 ? materials.PileSand_01
                 : object.name.startsWith('PileSand_02')
                   ? materials.PileSand_02
                   : object.name.startsWith('PileSand_03')
                     ? materials.PileSand_03
                     : materials[object.name];

      if (replacement) {
         object.material =
            object.name === 'KiesPlatteOffice'
               ? createKiesPlatteOfficeMaterial(replacement)
               : replacement;
      }
   });
}
